import React, { useMemo, useRef, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as logActions from 'actions/logActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import withSpinnerFeedback from 'HigherOrder/withSpinnerFeedback.jsx'
import { closeModal } from 'actions/modalActions'
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'

const DefaultModal = props => {
  const dispatch = useDispatch()
  const modalConfigs = useSelector(state => state.appConfig.system?.modals) || []

  const {
    config = {},
    style: propStyle,
    modal,
    tileConfig = { horizontal: '10px', vertical: '10px' }
  } = props
  const { title, closeable, content } = config

  const closeSelf = () => dispatch(closeModal(modal.address))

  const defaultModalContainerRef = useRef(0)
  const timerRef = useRef(null)
  const closeSelfRef = useRef(closeSelf)

  /// ///////////////////// life cycle effects
  useEffect(() => {
    // componentDidMount
    dispatch(logActions.addToClickLog({ event: `POP title: ${title}` }))
    return () => {
      // component will unmount
      clearTimeout(timerRef.current)
      closeSelfRef.current = () => null
    }
  }, [])

  const closeHandler = () => {
    if (defaultModalContainerRef.current) {
      defaultModalContainerRef.current.classList.remove('animate__bounceInDown')
      defaultModalContainerRef.current.classList.add('animate__bounceOutUp')
      timerRef.current = setTimeout(() => {
        closeSelfRef.current()
      }, 750)
    }
  }

  const passableProps = useMemo(() => {
    // modalConfigs doesn't require this to re-memoize because they should rarely change
    // an dont need to be monitored while a modal is open.
    if (content?.props && content.props.modalName != null) {
      const thisModalConfig = modalConfigs.find(
        config => config.modalName === content.props.modalName
      ) || {}
      // this modal has a name so its probably a configurable one.
      // fetch its config and merge it into the normal config
      return {
        config: {
          ...config,
          ...thisModalConfig
        },
        closeSelf: closeHandler
      }
    }
    return { closeSelf: closeHandler }
  }, [])

  /// //////// helper functions for this outer component
  const style = useMemo(() => {
    const zeroBasedPos = modal.stackPosition - 1
    const tile = {}
    if (tileConfig.horizontal) {
      const pixWidth = tileConfig.horizontal.replace('px', '')
      tile.left = zeroBasedPos * pixWidth + 'px'
    }
    if (tileConfig.vertical) {
      const pixWidth = tileConfig.vertical.replace('px', '')
      tile.top = zeroBasedPos * pixWidth + 'px'
    }
    return { ...propStyle, ...tile }
  }, [modal, propStyle, config.fullScreen])

  let widthClass = ''
  if (getCurrentScreenType() === 'ipad') {
    widthClass = 'max-dialog'
  } else {
    widthClass = window.innerWidth < 1600 ? 'wider' : ''
  }

  const mouseDownCapture = useCallback(e => {
    if (config.preventDefaultMouseDown) {
      // don't cause what's underneath to lose focus
      e.preventDefault()
      e.stopPropagation()
    }
  }, [config.preventDefaultMouseDown])

  const ContentComponent = useMemo(() => {
    let Comp
    if (typeof content === 'function') {
      Comp = withSpinnerFeedback('modal')((props) => content({ ...props, ...passableProps, isModal: true }))
      return <Comp { ...passableProps } />
    } else if (typeof content?.type === 'string') {
      // Plain DOM element (e.g. <div>...</div>) — don't inject closeSelf /
      // isModal as DOM attributes (React would warn about unknown props).
      return content
    } else if (typeof content.props === 'object') {
      Comp = withSpinnerFeedback('modal')((props) => React.cloneElement(content, { ...props, ...passableProps, isModal: true }))
      return <Comp { ...passableProps } />
    } else {
      return content
    }
  }, [content, passableProps])

  const specialClass = config.fullScreen ? 'full-screen' : ''

  return (
    <div
      ref={defaultModalContainerRef}
      style={style}
      className={`xtract-default-modal modal-container ${widthClass} animate__animated animate__bounceInDown ${
        config.containerClassName || ''
      } ${specialClass}`}
      onMouseDownCapture={mouseDownCapture}
    >
      <div className="list-component list-group">
        <div className="list-group-item active">
          <div className="header">
            <div className="corner-icons" />
            <h4 className="list-group-title">{title}</h4>
            <div className="corner-icons">
              {!closeable
                ? null
                : (
                <FontAwesomeIcon
                  icon={['fas', 'times']}
                  onClick={closeHandler}
                />
                  )}
            </div>
          </div>
        </div>
        <div className={'modal-content ' + (config.fullScreen ? 'full-screen' : '')}>
          {ContentComponent}
        </div>
      </div>
    </div>
  )
}

export default DefaultModal
